import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Briefcase,
  GraduationCap,
  MessageSquare,
  Sparkles,
  UserPlus,
  UserCheck,
  CheckCircle2,
  Calendar,
  Share2,
  HelpCircle,
  ArrowRight,
  ShieldCheck,
  Building,
  Layers,
} from 'lucide-react'
import { colors, radii, typography } from '../../styles/tokens'
import { alumniApi, type AlumniProfileItem } from '../../api/alumni'
import { usersApi } from '../../api/users'
import { Avatar } from '../../components/shared/Avatar'
import { VerifiedBadge } from '../../components/shared/VerifiedBadge'
import { AskQuestionModal } from '../../components/alumni/AskQuestionModal'
import { EmptyState } from '../../components/shared/EmptyState'
import { TimeAgo } from '../../components/shared/TimeAgo'
import toast from 'react-hot-toast'

export function AlumniProfileScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [alumnus, setAlumnus] = useState<AlumniProfileItem | null>(null)
  const [answers, setAnswers] = useState<any[]>([])
  const [questions, setQuestions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'experience' | 'answers' | 'tagged_questions'>('experience')

  const [isFollowing, setIsFollowing] = useState(false)
  const [followBusy, setFollowBusy] = useState(false)
  const [isAskModalOpen, setIsAskModalOpen] = useState(false)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      alumniApi.getById(id),
      alumniApi.getAnswers(id),
      alumniApi.getQuestions(id),
    ])
      .then(([profRes, ansRes, qRes]) => {
        const data = profRes.data.data
        setAlumnus(data)
        setIsFollowing(!!data.is_following)
        setAnswers(ansRes.data.data || [])
        setQuestions(qRes.data.data || [])
      })
      .catch((err) => {
        console.error('Failed to load alumni profile:', err)
        toast.error('Alumni profile not found')
      })
      .finally(() => setLoading(false))
  }, [id])

  const handleFollowToggle = async () => {
    if (!alumnus || followBusy) return
    setFollowBusy(true)
    const prev = isFollowing
    setIsFollowing(!prev)
    try {
      if (prev) {
        await usersApi.unfollow(alumnus.user_id)
        toast.success(`Unfollowed ${alumnus.full_name}`)
      } else {
        await usersApi.follow(alumnus.user_id)
        toast.success(`Following ${alumnus.full_name}`)
      }
    } catch {
      setIsFollowing(prev)
      toast.error('Failed to update follow status')
    } finally {
      setFollowBusy(false)
    }
  }

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${alumnus?.full_name} - Alumni Profile`,
          text: `Check out ${alumnus?.full_name}'s alumni profile on Campnect`,
          url,
        })
        return
      } catch {}
    }
    await navigator.clipboard.writeText(url)
    toast.success('Profile link copied to clipboard')
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 840, margin: '0 auto', padding: '30px 20px', width: '100%' }}>
        <div style={{ height: 260, borderRadius: 24, background: 'var(--color-surface)', animation: 'pulse 1.5s infinite' }} />
      </div>
    )
  }

  if (!alumnus) {
    return (
      <div style={{ maxWidth: 840, margin: '40px auto', padding: '20px', textAlign: 'center' }}>
        <EmptyState
          icon={<GraduationCap size={40} color="var(--color-text-muted)" />}
          title="Alumni Profile Not Found"
          description="The requested alumni profile could not be located or may have been removed."
          action={
            <button
              onClick={() => navigate('/app/alumni')}
              style={{
                padding: '8px 18px',
                borderRadius: radii.pill,
                border: 'none',
                background: '#111114',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Back to Alumni Network
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--color-bg)',
        paddingBottom: 'calc(var(--nav-height) + 40px)',
      }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px', width: '100%' }}>
        {/* Main Profile Header Card */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 24,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {/* Top row: Avatar + Name + Badges + Actions */}
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Avatar
              src={alumnus.avatar_url}
              name={alumnus.full_name}
              size={88}
            />

            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: '22px',
                    fontWeight: 800,
                    color: 'var(--color-text-primary)',
                    fontFamily: typography.fontDisplay,
                  }}
                >
                  {alumnus.full_name}
                </h1>

                {alumnus.is_verified === 1 && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: radii.pill,
                      background: '#111114',
                      color: '#FFFFFF',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    <VerifiedBadge size={14} /> Verified Alumni
                  </span>
                )}

                {alumnus.is_demo === 1 && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      padding: '2px 6px',
                    }}
                  >
                    Demo Profile
                  </span>
                )}
              </div>

              {/* Current Job Title and Company */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '6px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                }}
              >
                <Briefcase size={16} style={{ color: 'var(--color-text-secondary)' }} />
                <span>
                  {alumnus.job_title} at <strong>{alumnus.company}</strong>
                </span>
              </div>

              {/* College & Degree */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '4px',
                  fontSize: '13.5px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <GraduationCap size={16} />
                <span>
                  {alumnus.previous_college} · Class of {alumnus.graduation_year} ({alumnus.degree_course})
                </span>
              </div>

              {/* Industry */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '4px',
                  fontSize: '13px',
                  color: 'var(--color-text-muted)',
                }}
              >
                <Layers size={14} />
                <span>Industry: {alumnus.industry}</span>
              </div>
            </div>

            {/* Header Right Actions: Follow + Ask + Share */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <button
                onClick={handleShare}
                title="Share profile"
                style={{
                  padding: '9px',
                  borderRadius: '50%',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Share2 size={16} />
              </button>

              <button
                onClick={handleFollowToggle}
                disabled={followBusy}
                style={{
                  padding: '9px 18px',
                  borderRadius: radii.pill,
                  border: '1px solid',
                  borderColor: isFollowing ? 'var(--color-border)' : '#111114',
                  background: isFollowing ? 'transparent' : '#111114',
                  color: isFollowing ? 'var(--color-text-primary)' : '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: typography.fontDisplay,
                  cursor: followBusy ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                {isFollowing ? (
                  <>
                    <UserCheck size={14} /> Following
                  </>
                ) : (
                  <>
                    <UserPlus size={14} /> Follow
                  </>
                )}
              </button>

              <button
                onClick={() => setIsAskModalOpen(true)}
                style={{
                  padding: '9px 20px',
                  borderRadius: radii.pill,
                  border: 'none',
                  background: '#111114',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: typography.fontDisplay,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <MessageSquare size={14} /> Ask Question
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              padding: '12px 18px',
              borderRadius: radii.md,
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
            }}
          >
            <div>
              <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
                {alumnus.questions_answered_count || answers.length}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>
                Questions Answered
              </span>
            </div>

            <div style={{ width: 1, height: 24, background: 'var(--color-border)' }} />

            <div>
              <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
                {alumnus.helpful_votes_count || answers.reduce((s, a) => s + (a.helpful_count || 0), 0)}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>
                Helpful Votes
              </span>
            </div>

            <div style={{ width: 1, height: 24, background: 'var(--color-border)' }} />

            <div>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                Status: Verified Mentor
              </span>
            </div>
          </div>

          {/* Bio */}
          {alumnus.bio && (
            <div>
              <h3 style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                About
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: 'var(--color-text-secondary)',
                }}
              >
                {alumnus.bio}
              </p>
            </div>
          )}

          {/* Skills & Expertise */}
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
              Areas of Expertise & Skills
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(alumnus.expertise || []).map((exp, i) => (
                <span
                  key={`exp-${i}`}
                  style={{
                    padding: '4px 12px',
                    borderRadius: radii.pill,
                    background: '#111114',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {exp}
                </span>
              ))}
              {(alumnus.skills || []).map((sk, i) => (
                <span
                  key={`sk-${i}`}
                  style={{
                    padding: '4px 12px',
                    borderRadius: radii.pill,
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-primary)',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {sk}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Section Tabs: Experience vs Answers */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            margin: '24px 0 16px',
            borderBottom: '1px solid var(--color-border)',
            paddingBottom: '8px',
          }}
        >
          <button
            onClick={() => setActiveTab('experience')}
            style={{
              padding: '8px 16px',
              borderRadius: radii.pill,
              border: 'none',
              background: activeTab === 'experience' ? '#111114' : 'transparent',
              color: activeTab === 'experience' ? '#fff' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: typography.fontDisplay,
              cursor: 'pointer',
            }}
          >
            Career Experience
          </button>

          <button
            onClick={() => setActiveTab('answers')}
            style={{
              padding: '8px 16px',
              borderRadius: radii.pill,
              border: 'none',
              background: activeTab === 'answers' ? '#111114' : 'transparent',
              color: activeTab === 'answers' ? '#fff' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: typography.fontDisplay,
              cursor: 'pointer',
            }}
          >
            Public Answers ({answers.length})
          </button>

          <button
            onClick={() => setActiveTab('tagged_questions')}
            style={{
              padding: '8px 16px',
              borderRadius: radii.pill,
              border: 'none',
              background: activeTab === 'tagged_questions' ? '#111114' : 'transparent',
              color: activeTab === 'tagged_questions' ? '#fff' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: typography.fontDisplay,
              cursor: 'pointer',
            }}
          >
            Questions Tagged In ({questions.length})
          </button>
        </div>

        {/* TAB CONTENT: CAREER EXPERIENCE */}
        {activeTab === 'experience' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {alumnus.experience && alumnus.experience.length > 0 ? (
              alumnus.experience.map((exp, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 18,
                    padding: '18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                        {exp.role}
                      </h4>
                      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                        {exp.company}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-text-muted)',
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        padding: '2px 8px',
                        borderRadius: radii.pill,
                      }}
                    >
                      {exp.duration}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
                    {exp.description}
                  </p>
                </div>
              ))
            ) : (
              <EmptyState
                icon={<Briefcase size={36} color="var(--color-text-muted)" />}
                title="No additional experience listed"
                description="Current role is displayed in the primary profile header above."
              />
            )}
          </div>
        )}

        {/* TAB CONTENT: PUBLIC ANSWERS */}
        {activeTab === 'answers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {answers.length > 0 ? (
              answers.map((ans) => (
                <div
                  key={ans.id}
                  onClick={() => navigate(`/app/alumni/questions/${ans.question_id}`)}
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 18,
                    padding: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    transition: 'border-color 160ms ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(17,17,20,0.25)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: radii.pill,
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      {ans.question_category || 'Q&A Discussion'}
                    </span>
                    <span style={{ fontSize: '11.5px', color: 'var(--color-text-muted)' }}>
                      <TimeAgo date={ans.created_at} />
                    </span>
                  </div>

                  <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {ans.question_title}
                  </h4>

                  <p
                    style={{
                      margin: 0,
                      fontSize: '13.5px',
                      lineHeight: 1.6,
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {ans.content}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '8px',
                      borderTop: '1px solid var(--color-border)',
                      fontSize: '12px',
                    }}
                  >
                    <span style={{ color: '#15803D', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Sparkles size={13} /> {ans.helpful_count || 0} helpful votes
                    </span>
                    <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      View Discussion <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                icon={<HelpCircle size={36} color="var(--color-text-muted)" />}
                title="No public answers yet"
                description="Ask this alumnus a question to get their career guidance!"
                action={
                  <button
                    onClick={() => setIsAskModalOpen(true)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Ask a Question
                  </button>
                }
              />
            )}
          </div>
        )}

        {/* TAB CONTENT: QUESTIONS TAGGED IN */}
        {activeTab === 'tagged_questions' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {questions.length > 0 ? (
              questions.map((q) => (
                <div
                  key={q.id}
                  onClick={() => navigate(`/app/alumni/questions/${q.id}`)}
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 18,
                    padding: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-text-muted)' }}>
                    {q.category} · Asked by {q.student_name}
                  </div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {q.title}
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                    {q.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '12px', fontWeight: 600 }}>
                    Open Question <ArrowRight size={13} style={{ marginLeft: 4 }} />
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                icon={<MessageSquare size={36} color="var(--color-text-muted)" />}
                title="No questions tagged yet"
                description="Tag this alumnus when submitting a question to notify them."
                action={
                  <button
                    onClick={() => setIsAskModalOpen(true)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Ask a Question
                  </button>
                }
              />
            )}
          </div>
        )}
      </div>

      {/* Ask Question Modal */}
      <AskQuestionModal
        isOpen={isAskModalOpen}
        onClose={() => setIsAskModalOpen(false)}
        initialTargetAlumnus={alumnus}
        onSuccess={(newQId) => {
          navigate(`/app/alumni/questions/${newQId}`)
        }}
      />
    </div>
  )
}
